import React, { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { getAccommodations, setAccommodations } from '../../lib/api';
import { toast } from 'sonner';
import { DemoChip } from '../ui/demo-chip';
import { studentLabel } from '../../lib/format';

export const AccommodationsPanel = ({ studentDrafts }) => {
  const [accStudentId, setAccStudentId] = useState('');
  const [accData, setAccData] = useState(null);
  const [accLoading, setAccLoading] = useState(false);

  return (
    <div className="card-surface p-6">
      <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Lightbulb className="text-gold-bright" size={20} />
        Student Accommodations (504/IEP)
      </h3>
      <p className="text-slate-400 text-sm mb-4">
        Configure writing supports for students with 504 plans or IEPs on this assignment.
      </p>

      <div className="flex items-center gap-4 mb-4">
        <select
          data-testid="acc-student-select"
          value={accStudentId}
          onChange={async (e) => {
            const sid = e.target.value;
            setAccStudentId(sid);
            if (!sid) { setAccData(null); return; }
            setAccLoading(true);
            try {
              const data = await getAccommodations(sid);
              setAccData(data);
            } catch {
              setAccData({ extended_time: false, audio_reader: false, sentence_starters: false, graphic_organizer: false });
            } finally {
              setAccLoading(false);
            }
          }}
          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm"
        >
          <option value="">Select a student...</option>
          {studentDrafts.map(d => (
            <option key={d.student_id} value={d.student_id}>{studentLabel(d)}</option>
          ))}
        </select>
      </div>

      {accLoading && <div className="text-slate-400 text-sm py-4">Loading accommodations...</div>}

      {accData && !accLoading && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'extended_time', label: 'Extended Time (1.5x)', desc: 'Gives additional time on timed writing' },
            { key: 'audio_reader', label: 'Audio Reader (TTS)', desc: 'Read-aloud support for draft content' },
            { key: 'sentence_starters', label: 'Sentence Starters', desc: 'Provides academic sentence frames' },
            { key: 'graphic_organizer', label: 'Graphic Organizer', desc: 'Visual planning tools for pre-writing' },
          ].map(acc => (
            <div key={acc.key} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-white text-sm font-medium">{acc.label}</span>
                <DemoChip label="Sign in to update accommodations">
                  <button
                    data-testid={`acc-toggle-${acc.key}`}
                    role="switch"
                    aria-checked={accData[acc.key]}
                    aria-label={acc.label}
                    onClick={async () => {
                      const updated = { ...accData, [acc.key]: !accData[acc.key], student_id: accStudentId };
                      setAccData(updated);
                      try {
                        await setAccommodations(updated);
                        toast.success(`${acc.label} ${updated[acc.key] ? 'enabled' : 'disabled'}`);
                      } catch {
                        toast.error('Failed to update accommodation.');
                      }
                    }}
                    className={`w-10 h-5 rounded-full relative transition-colors ${
                      accData[acc.key] ? 'bg-flow-blue' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                      accData[acc.key] ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </DemoChip>
              </div>
              <p className="text-slate-500 text-xs">{acc.desc}</p>
            </div>
          ))}
        </div>
      )}

      {!accStudentId && !accLoading && (
        <div className="text-center py-4">
          <p className="text-slate-500 text-sm">Select a student above to view and manage their writing accommodations.</p>
        </div>
      )}
    </div>
  );
};
